<script>
import { onMount, afterUpdate } from "svelte";
import ChatMessages from "./ChatMessages.svelte";
import ChatInput from "./ChatInput.svelte";

let messages = [];
let isLoading = false;
let error = null;
let chatContainer;
let clinicalNotes = "";
let selectedCodeType = "ICD-10-CM";
let ai_index = 0;
let files = [];
let stopRequested = false;

let isMobile = window.innerWidth <= 968;

// Demo mode variables
export let isDemoMode = false;
let demoNotes = "Patient is a 67-year-old male with a history of hypertension and type 2 diabetes mellitus presenting with acute onset of chest pain radiating to the left arm, diaphoresis, and shortness of breath. ECG shows ST elevation in leads V1-V4. Initial troponin is elevated at 0.8 ng/mL.";
let demoResponse = "**Recommended ICD-10-CM Codes:**\n\n1. **I21.3** - ST elevation (STEMI) myocardial infarction of unspecified site\n   - Primary diagnosis based on ECG findings of ST elevation and elevated troponin\n\n2. **I10** - Essential (primary) hypertension\n   - Documented in patient history\n\n3. **E11.9** - Type 2 diabetes mellitus without complications\n   - Documented in patient history\n\n4. **R07.9** - Chest pain, unspecified\n   - Presenting symptom\n\n5. **R06.02** - Shortness of breath\n   - Presenting symptom\n\n**Note:** Consider further specificity for the STEMI diagnosis pending additional documentation of affected wall or coronary artery.";

let csrf_token;
// Manage multiple sessions
let history = [];

let currentSessionId = Date.now();

onMount(() => {
    csrf_token = document.querySelector(
        'input[name="csrfmiddlewaretoken"]',
    )?.value;
});

afterUpdate(() => {
    if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
    }
});

// Send message to backend
async function handleSend() {
    // If in demo mode, ignore actual sending and just show predefined response
    if (isDemoMode) {
        
        isLoading = true;
        messages.push({ role: "user", content: demoNotes, attachments: [] });
        demoNotes = ""; // Clear input
        messages = [...messages];
        
        // Add empty AI message that will be gradually filled
        setTimeout(() => {
            messages.push({ role: "ai", content: "" });
            messages = [...messages];
            ai_index = messages.length - 1;
            
            // Simulate typing character by character
            const typingSpeed = 20; // milliseconds per character
            let currentIndex = 0;
            
            // Start typing animation
            const typingInterval = setInterval(() => {
                // Add the next character
                if (currentIndex < demoResponse.length) {
                    messages[ai_index].content += demoResponse[currentIndex];
                    messages = [...messages]; // Trigger UI update
                    currentIndex++;
                } else {
                    // Typing complete, clear interval
                    clearInterval(typingInterval);
                    isLoading = false;
                    // show a call to action to create an account
                    setTimeout(() => {
                        messages.push({
                            role: "ai",
                            content: "Ready to try HippoCode yourself? <a href='/accounts/signup'>Sign up for a free account</a> to get started!",
                        });
                        messages = [...messages];
                    }, 1000);
                }
            }, typingSpeed);
        }, 1000);
        
        return;
    }
    
    // Regular functionality for non-demo mode
    isLoading = true;
    let clinical_notes_copy = clinicalNotes;
    clinicalNotes = "";
    messages.push({ role: "user", content: clinical_notes_copy, attachments: files });
    messages = [...messages];
    stopRequested = false;
    try {
        let formData = new FormData();
        formData.append("clinical_notes", clinical_notes_copy);
        formData.append("coding_system", selectedCodeType);
        
        for (const file of files) {
            formData.append("files", file);
        }
        files = [];
        const response = await fetch("/stream_codes", {
            method: "POST",
            headers: {
                "X-CSRFToken": csrf_token,
            },
            body: formData,
        });

        
        if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
        }
        // Read the response body as a stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let done = false;
        
        messages.push({ role: "ai", content: "" });
        ai_index = messages.length - 1;
        while (!done) {
            if(stopRequested){
                await reader.cancel();
                stopRequested = false; // Reset stop flag
                break; // Exit the loop if stop was requested
            }
            const { value, done: doneReading } = await reader.read();
            done = doneReading;
            if (value) {
                // Decode the chunk into text
                const chunk = decoder.decode(value, { stream: true });
                // Append it to our output DIV
                messages[ai_index].content += chunk;
                messages = [...messages];
            }
        }
    } catch (error) {
        console.error("Error while streaming:", error);

    } finally {
        isLoading = false;
    }
}

// Save current conversation to history
function saveSession() {
    if (!messages.length) return;
    const snippet =
        messages.find((m) => m.role === "user")?.content.slice(0, 50) ||
        "New Chat";

    const newSession = {
        id: currentSessionId,
        title: snippet,
        messages: [...messages],
    };
    history = [newSession, ...history];

    // Reset
    messages = [];
    currentSessionId = Date.now();
}

function handleStop() {
    stopRequested = true;
}
</script>

<div class="row justify-content-center flex-fill">

    <!-- Main Chat Area -->
    <div class="{isDemoMode ? '' : 'col-lg-6 col-md-12'} text-center">
        {#if messages.length === 0 && !isLoading}
            <!-- Welcome Screen: displayed if no conversation yet -->
            <div class="welcome-screen" style="margin-top: {isDemoMode ? '0px' : '20vh'}">
                <img src="static/images/hippocode.png" alt="Hippocode Logo" class="img-fluid" style="max-width: 50px;" />
                <i class="bi-chat-dots-fill welcome-icon"></i>
                {#if !isDemoMode || !isMobile } 
                    <h2 class="mt-3">Welcome to HippoCode</h2>
                {/if}
                <p class="text-muted">
                    Enter your clinical notes to automatically generate medical codes.
                </p>

                <!-- Large input to get started -->
                <div class="mt-4">
                    <ChatInput
                        {isLoading}
                        placeholder="Describe the patient's condition or notes here..."
                        bind:clinicalNotes
                        bind:selectedCodeType
                        bind:files
                        on:send={handleSend}
                        on:stop={handleStop}
                        {isDemoMode}
                        bind:demoText={demoNotes}
                    />
                </div>
            </div>
        {:else}
            <!-- Chat area with messages -->
            <ChatMessages bind:chatContainer {messages} {isLoading} />

            {#if error}
                <div class="alert alert-danger mt-3">{error}</div>
            {/if}

            <!-- Sticky input + "Save" button -->
            <div class="pt-2 pb-3">
                <ChatInput
                    {isLoading}
                    placeholder="Describe the patient's condition or notes here..."
                    bind:clinicalNotes
                    bind:selectedCodeType
                    bind:files
                    on:send={handleSend}
                    on:stop={handleStop}
                    {isDemoMode}
                    bind:demoText={demoNotes}
                    hasRanOnce={true}
                />
            </div>
        {/if}
    </div>
</div>